import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { AlertTriangle, ShieldOff, Lock, Trash2, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

const SystemReset = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const [adminPassword, setAdminPassword] = useState('');
  const [confirmToken, setConfirmToken] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const e = await axiosInstance.get('/system/reset/enabled');
        setEnabled(e.data.enabled);
        if (e.data.enabled) {
          const p = await axiosInstance.get('/system/reset/preview');
          setPreview(p.data);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'فشل تحميل بيانات إعادة التعيين');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const canSubmit =
    enabled === true &&
    !submitting &&
    !result &&
    acknowledged &&
    confirmToken === 'RESET' &&
    adminPassword.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!window.confirm('⚠️ هل أنت متأكد تماماً؟ سيتم حذف جميع البيانات التشغيلية بشكل نهائي.\n\nThis cannot be undone except by restoring the auto-backup.')) return;
    setSubmitting(true);
    try {
      const r = await axiosInstance.post('/system/reset', {
        admin_password: adminPassword,
        confirmation_token: confirmToken,
      });
      setResult(r.data);
      toast.success('تم تهيئة النظام بنجاح — System reset complete');
      // refresh preview
      try {
        const p = await axiosInstance.get('/system/reset/preview');
        setPreview(p.data);
      } catch (e) { /* non-fatal: preview refresh */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل التنفيذ');
    } finally {
      setSubmitting(false);
      setAdminPassword('');
      setConfirmToken('');
      setAcknowledged(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="system-reset-page">
      <button
        onClick={() => navigate('/settings')}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        data-testid="back-to-settings"
      >
        <ArrowLeft className="w-4 h-4" /> رجوع إلى الإعدادات
      </button>

      <div className="bg-gradient-to-r from-rose-50 to-red-50 border-2 border-rose-300 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-rose-100 rounded-full">
            <AlertTriangle className="w-8 h-8 text-rose-700" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-rose-900">{t('systemreset.system_reset')}</h1>
            <p className="text-rose-700 mt-2 leading-relaxed">
              عملية تدميرية — تحذف جميع البيانات التشغيلية والتجريبية للبدء من جديد
              في بيئة الإنتاج. لا تنفّذها إلا بعد التأكد من أن النظام جاهز للاستخدام
              الفعلي وأنك لا تحتاج أي بيانات حالية.
            </p>
          </div>
        </div>
      </div>

      {enabled === false && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <ShieldOff className="w-6 h-6 text-amber-700" />
            <div>
              <h3 className="font-bold text-amber-900 text-lg">معطّل في هذه البيئة</h3>
              <p className="text-amber-800 mt-1">
                إعادة تعيين النظام معطلة. لتفعيلها، يجب تعيين متغير البيئة{' '}
                <code className="bg-amber-200 px-1 rounded">ENABLE_SYSTEM_RESET=true</code>{' '}
                ثم إعادة تشغيل الـ Backend.
              </p>
            </div>
          </div>
        </div>
      )}

      {enabled && preview && (
        <>
          {/* Post-wipe COA re-seed notice */}
          {preview?.post_wipe_actions?.reseed_chart_of_accounts && (
            <div
              className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4 flex items-start gap-3"
              data-testid="coa-reseed-notice"
            >
              <CheckCircle className="w-5 h-5 text-indigo-700 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-indigo-900">
                  إعادة بناء شجرة الحسابات تلقائياً
                </p>
                <p className="text-indigo-700 mt-1 leading-relaxed">
                  سيتم حذف الحسابات القديمة بالكامل، ثم إعادة بناء شجرة الحسابات
                  من القالب القياسي السعودي للمقاولات تلقائياً بعد المسح — لتعود
                  صفحة الحسابات نظيفة وجاهزة للاستخدام الفوري.
                </p>
                {preview.post_wipe_actions.reseed_for_companies?.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-1 font-mono">
                    لـ {preview.post_wipe_actions.reseed_for_companies.length} شركة
                  </p>
                )}
              </div>
            </div>
          )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Will Preserve */}
          <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-sm overflow-hidden">
            <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-200 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-700" />
              <h3 className="font-bold text-emerald-900">سيتم الاحتفاظ به</h3>
            </div>
            <div className="p-4 space-y-1 max-h-96 overflow-y-auto text-sm">
              {Object.entries(preview.will_preserve).sort().map(([col, n]) => (
                <div key={col} className="flex justify-between border-b border-gray-50 py-1">
                  <span className="text-gray-700 font-mono text-xs">{col}</span>
                  <span className="font-mono font-semibold text-emerald-700">{n}</span>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t border-dashed text-xs text-gray-600">
                + Admin: {preview.admin_users_kept} مستخدم
              </div>
            </div>
          </div>

          {/* Will Delete */}
          <div className="bg-white rounded-xl border-2 border-rose-200 shadow-sm overflow-hidden">
            <div className="bg-rose-50 px-4 py-3 border-b border-rose-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-rose-700" />
                <h3 className="font-bold text-rose-900">سيتم حذفه</h3>
              </div>
              <span className="text-xs bg-rose-200 text-rose-900 font-bold px-2 py-1 rounded-full" data-testid="total-delete-count">
                {preview.will_delete_total} سجل
              </span>
            </div>
            <div className="p-4 space-y-1 max-h-96 overflow-y-auto text-sm">
              {Object.entries(preview.will_delete)
                .filter(([, n]) => n > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([col, n]) => (
                  <div key={col} className="flex justify-between border-b border-gray-50 py-1">
                    <span className="text-gray-700 font-mono text-xs">{col}</span>
                    <span className="font-mono font-semibold text-rose-700">{n}</span>
                  </div>
                ))}
              <div className="mt-3 pt-3 border-t border-dashed text-xs text-gray-600">
                + {preview.uploads_to_delete.file_count} ملف مرفق
                ({preview.uploads_to_delete.total_size_mb} MB)
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {enabled && !result && (
        <form onSubmit={submit} className="bg-white rounded-xl border-2 border-rose-300 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-bold text-rose-900 flex items-center gap-2">
            <Lock className="w-5 h-5" /> تأكيد التنفيذ
          </h2>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="acknowledge-checkbox">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 w-5 h-5 accent-rose-600"
              data-testid="acknowledge-input"
            />
            <span className="text-sm text-gray-800 leading-relaxed">
              أُقرّ بأنني فهمت أن هذه العملية ستحذف جميع البيانات التشغيلية بشكل
              نهائي، وأن نسخة احتياطية كاملة ستُنشأ تلقائياً قبل التنفيذ، وأنني
              قمت بقراءة كافة العناصر التي ستُحذف في الجدول أعلاه.
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                كلمة مرور الأدمن <span className="text-rose-600">*</span>
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full border-2 border-gray-200 focus:border-rose-400 rounded-lg px-3 py-2 text-sm"
                placeholder="أدخل كلمة مرور الأدمن الحالية"
                data-testid="admin-password-input"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                اكتب <code className="bg-gray-100 text-rose-700 font-bold px-1 rounded">RESET</code> للتأكيد <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                value={confirmToken}
                onChange={(e) => setConfirmToken(e.target.value)}
                className={`w-full border-2 rounded-lg px-3 py-2 text-sm font-mono ${
                  confirmToken === 'RESET' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 focus:border-rose-400'
                }`}
                placeholder="اكتب RESET بأحرف كبيرة"
                data-testid="confirm-token-input"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            🛡️ سيتم إنشاء <strong>نسخة احتياطية كاملة</strong> تلقائياً قبل التنفيذ.
            في حال حدث أي خطأ، يمكنك استعادتها من صفحة <strong>النسخ الاحتياطي</strong>.
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="px-5 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="execute-reset-btn"
              className={`px-5 py-2.5 rounded-lg font-bold text-white flex items-center gap-2 ${
                canSubmit
                  ? 'bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" /> جاري التنفيذ…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" /> تنفيذ إعادة التعيين
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {result && (
        <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm p-6" data-testid="reset-result">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-6 h-6 text-emerald-700" />
            <h2 className="text-lg font-bold text-emerald-900">تمت إعادة التعيين بنجاح</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-xs text-emerald-700">إجمالي السجلات المحذوفة</div>
              <div className="font-mono font-bold text-emerald-900 text-xl">{result.report.records_deleted_total}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-xs text-blue-700">ملفات محذوفة من القرص</div>
              <div className="font-mono font-bold text-blue-900 text-xl">{result.report.uploads_deleted.file_count}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="text-xs text-purple-700">حجم محرر</div>
              <div className="font-mono font-bold text-purple-900 text-xl">{result.report.uploads_deleted.size_mb} MB</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-xs text-amber-700">النسخة الاحتياطية</div>
              <div className="font-mono font-bold text-amber-900 text-xs break-all">{result.report.backup_file || '—'}</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-600">
            معرّف سجل التدقيق: <code className="bg-gray-100 px-1 rounded">{result.report.audit_log_entry_id}</code>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
          >
            الذهاب إلى لوحة التحكم
          </button>
        </div>
      )}
    </div>
  );
};

export default SystemReset;
